import { useLayoutEffect, useState } from 'react';

/**
 * Decides when a live webcam player may mount.
 * Standalone embeds mount immediately. Carousel embeds wait until the slide is
 * active and Swiper has finished transitioning so the player measures final size.
 *
 * Swiper may not attach its instance until after the first layout, so we retry
 * binding briefly instead of giving up and leaving the player stuck on preview.
 */
export const useWebcamPlayerMount = ({ enabled, frameRef }) => {
  const [shouldMountPlayer, setShouldMountPlayer] = useState(false);
  const [playerKey, setPlayerKey] = useState(0);

  useLayoutEffect(() => {
    if (!enabled) {
      setShouldMountPlayer(false);
      return undefined;
    }

    const slide = frameRef.current?.closest('.swiper-slide');
    if (!slide) {
      setShouldMountPlayer(true);
      return undefined;
    }

    let swiper = null;
    let retryId = null;
    let removed = false;

    const isActiveSlide = () => slide.classList.contains('swiper-slide-active');

    const activate = () => {
      setPlayerKey((key) => key + 1);
      setShouldMountPlayer(true);
    };
    const deactivate = () => setShouldMountPlayer(false);

    const syncSettled = () => {
      if (removed) return;
      if (isActiveSlide() && !swiper?.animating) {
        setShouldMountPlayer(true);
      } else if (!isActiveSlide()) {
        deactivate();
      }
    };

    const onSlideChange = () => {
      if (!isActiveSlide()) deactivate();
    };
    const onTransitionEnd = () => {
      if (isActiveSlide()) activate();
    };

    const bindSwiper = (instance) => {
      if (removed || swiper) return;
      swiper = instance;
      swiper.on('slideChange', onSlideChange);
      swiper.on('slideChangeTransitionEnd', onTransitionEnd);
      syncSettled();
    };

    const tryBind = () => {
      const instance = slide.closest('.swiper')?.swiper;
      if (!instance) return false;
      bindSwiper(instance);
      return true;
    };

    if (!tryBind()) {
      // Class may already be active before Swiper publishes its instance.
      syncSettled();
      let attempts = 0;
      retryId = setInterval(() => {
        attempts += 1;
        if (tryBind() || attempts >= 40 || removed) {
          clearInterval(retryId);
          retryId = null;
        }
      }, 50);
    }

    return () => {
      removed = true;
      if (retryId) clearInterval(retryId);
      if (swiper) {
        swiper.off('slideChange', onSlideChange);
        swiper.off('slideChangeTransitionEnd', onTransitionEnd);
      }
    };
  }, [enabled, frameRef]);

  return { shouldMountPlayer, playerKey };
};
